import { useState, useEffect } from 'react'
import { useAuth } from '@/context/auth-context'
import { useOnboarding } from '@/context/onboarding-context'
import { useTheme } from '@/context/theme-context'
import { Navigate, useLocation } from 'react-router-dom'
import type { ProtectedRouteProps } from '@/types'

// Minimum time to show splash screen (ms) for smoother UX
const MIN_SPLASH_DURATION = 800

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth()
  const { isComplete: onboardingComplete, loading: onboardingLoading } = useOnboarding()
  const { theme } = useTheme()
  const location = useLocation()
  
  // Track minimum splash duration
  const [minTimeElapsed, setMinTimeElapsed] = useState(false)
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinTimeElapsed(true)
    }, MIN_SPLASH_DURATION)
    
    return () => clearTimeout(timer)
  }, [])

  // Get the current effective theme (including system theme)
  const getCurrentTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme
  }

  // Get the appropriate logo based on theme
  const getLogoSrc = () => {
    const currentTheme = getCurrentTheme()
    return currentTheme === 'dark' 
      ? '/Calintent-transparent-dark-teal-400.png'
      : '/Calintent-transparent-light-teal-500.png'
  }

  // Determine if we're still in the "deciding" phase
  const isLoading = authLoading || onboardingLoading
  const showSplash = isLoading || !minTimeElapsed

  // Show loading state while auth or onboarding is being checked, 
  // or while minimum splash duration hasn't elapsed
  if (showSplash) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center animate-in fade-in duration-300">
          <img 
            src={getLogoSrc()} 
            alt="Calintent" 
            className="h-20 w-auto mx-auto mb-8 drop-shadow-sm"
          />
          <div className="flex justify-center mb-4">
            <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-muted border-t-primary"></div>
          </div>
          <p className="text-sm text-muted-foreground font-medium">Getting things ready...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    // Preserve fromExtension parameter if present
    const searchParams = new URLSearchParams(location.search)
    const fromExtension = searchParams.get('fromExtension')
    const loginPath = fromExtension === '1' ? '/login?fromExtension=1' : '/login'
    
    // Redirect to login page with return url
    return <Navigate to={loginPath} state={{ from: location }} replace />
  }

  // Redirect to onboarding if incomplete (but not if already on /onboarding)
  if (!onboardingComplete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  // Redirect away from /onboarding if already complete (go directly to insights)
  if (onboardingComplete && location.pathname === '/onboarding') {
    return <Navigate to="/insights" replace />
  }

  return <>{children}</>
}
